import React, { type JSX } from 'react'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { Link, useLocation } from 'react-router'
import { Grid, GridCell, GridRow } from '@mobi/component-library/Legacy/Grid'
import { hexColors } from '@mobi/settings'
import { TippingPoolHeader } from '@core/Areas/Tipping/Components/Common/TippingPoolHeader'
import { NoticeBox } from '@core/Components/NoticeBox'
import { queryKeys } from '@core/Data/ReactQuery/constants'
import { appHistory } from '@core/Utils/routing/history'
import { ResultsDateStyled, RowContainerStyled } from '../../TippingPage.styles'
import { ResultedPoolsResponse, getResultedPools, queryDefaults } from '../../data/toteSports'
import { LoadingBars } from '../Common/LoadingBars'
import { DateNavigation } from './Components/DateNavigation'
import { SingleResultedPool } from './Components/SingleResultedPool'

const SHORT_DATE_FORMAT = 'ddd, DD MMM YYYY'

export const ResultedPoolList = (): JSX.Element => {
  const path = useLocation().pathname

  const selectedDate = new URLSearchParams(useLocation().search).get('date')

  const { isLoading, isError, data } = useQuery<ResultedPoolsResponse>({
    ...queryDefaults,
    queryKey: [queryKeys.toteSportsResultedPools, { date: selectedDate }],
    queryFn: async () => await getResultedPools(selectedDate || dayjs().format('YYYY-MM-DD')),
  })

  if (isLoading) {
    return (
      <>
        <DateDisplay data={data} selectedDate={selectedDate} />

        <div data-testid='ResultedPoolList.Loading'>
          <LoadingBars />
        </div>
      </>
    )
  }

  if (isError) {
    return (
      <>
        <DateDisplay data={data} selectedDate={selectedDate} />
        <GridCell padding='1rem'>
          <NoticeBox
            title='Unable to display pools at this time. Please try again later.'
            noticeBoxType='Warning'
            hasBorder
          />
        </GridCell>
      </>
    )
  }

  return (
    <div style={{ backgroundColor: hexColors.white }}>
      <DateDisplay data={data} selectedDate={selectedDate} />

      {!data?.sports?.length && (
        <GridCell padding='1rem'>
          <NoticeBox
            title='There are no pools available for this date.'
            noticeBoxType='Info'
            hasBorder
          />
        </GridCell>
      )}

      {data?.sports.map(
        sport =>
          sport.pools?.length > 0 && (
            <div key={sport.key}>
              <TippingPoolHeader
                description={sport.description}
                icon={sport.icon}
                data-tid-tote-sport-header
              />

              <RowContainerStyled>
                <Grid>
                  <GridRow>
                    <GridCell align='left' />
                    <GridCell width='25%' align='left' data-tid-tote-sport-pool-div-official-title>
                      Divs Official at
                    </GridCell>
                    <GridCell
                      width='16.6666%'
                      align='center'
                      data-tid-tote-sport-pool-tips-req-title
                    >
                      Tips Req
                    </GridCell>
                    <GridCell width='8.3333%' />
                    <GridCell
                      width='16.6666%'
                      align='right'
                      data-tid-tote-sport-pool-dividend-title
                    >
                      Dividend
                    </GridCell>
                    <GridCell width='3em' />
                  </GridRow>
                </Grid>
              </RowContainerStyled>

              {sport.pools.map(pool => (
                <Link
                  key={pool.key}
                  style={{ textDecoration: 'none' }}
                  to={`${path}/${sport.icon.toLowerCase()}/${pool.key}`}
                >
                  <SingleResultedPool key={pool.key} pool={pool} />
                </Link>
              ))}
            </div>
          )
      )}
    </div>
  )
}

// Local Component

const DateDisplay = ({
  data,
  selectedDate,
}: {
  data: ResultedPoolsResponse | undefined
  selectedDate: string | null
}): JSX.Element => (
  <>
    <ResultsDateStyled>
      {data?.startDate && dayjs(data.startDate).format(SHORT_DATE_FORMAT)}&ndash;
      {data?.endDate && dayjs(data.endDate).format(SHORT_DATE_FORMAT)}
    </ResultsDateStyled>

    <DateNavigation
      date={dayjs(selectedDate || '').toDate()}
      onDateChange={date =>
        appHistory.push({ search: `?date=${dayjs(date).format('YYYY-MM-DD')}` })
      }
    />
  </>
)
